<template>
  <div
    ref="collapseRef"
    class="section-list-collapsible flex flex-col gap-4 w-full pa-4"
    :class="{ 'mt-[50px]': isEditMode && componentSelectedMode && componentSelectedMode.id === id }"
  >
    <div v-if="sectionListName" class="tracking-wide font-bold uppercase text-truncate">
      {{ sectionListName }}
    </div>

    <div
      v-for="{ section, index: sectionIdx } in sections"
      :key="section.id + sectionIdx"
      class="canvas-section section-list-collapsible-item relative !border-none rounded-2"
      :data-section-id="section.id"
      :data-section-index="sectionIdx"
      :name="section.id"
    >
      <SectionInfoProvider :section-idx="sectionIdx" :section-list-id="id" :used-in-section-id="section.id">
        <div
          class="flex justify-between items-center cursor-pointer relative"
          :class="{
            'py-2': !listHeaderHtmlBySectionId[section.id],
          }"
          @click="toggleSection(section.id)"
        >
          <SectionListExecution
            v-if="canvasSectionExecutionApp"
            :executionApp="canvasSectionExecutionApp"
            :section="section"
          />
          <div
            v-if="listHeaderHtmlBySectionId[section.id]"
            class="section-list-collapsible-item__header py-4 pl-4 basis-full"
            :class="{
              'c-theme-preset--h_1': maintainSectionTheme && expandedSectionId === section.id,
            }"
            :style="
              expandedSectionId === section.id ? defaultSectionHeaderExpandedStyle : defaultSectionHeaderCollapsedStyle
            "
            v-html="listHeaderHtmlBySectionId[section.id]"
          />
          <div
            v-else-if="pitcherSettings?.canvas?.show_fallback_section_name_on_collapsibles || mode === 'sharing'"
            class="flex items-center gap-2 font-size-[20px] font-bold color-primary"
            :class="{
              'c-theme-preset--h_1': maintainSectionTheme && expandedSectionId === section.id,
            }"
            :style="
              expandedSectionId === section.id ? defaultSectionHeaderExpandedStyle : defaultSectionHeaderCollapsedStyle
            "
          >
            <div
              v-if="section?.thumbnail_url"
              class="w-[60px] h-[60px] flex items-center justify-center border-solid border-primary6 rounded-1 overflow-hidden"
            >
              <img class="max-w-[60px] max-h-[60px] object-contain" :src="section.thumbnail_url" />
            </div>
            {{ section.name }}
          </div>
          <CButton circle class="mr-4" size="large">
            <template #icon>
              <CIcon :icon="expandedSectionId === section.id ? 'angle-up' : 'angle-down'" />
            </template>
          </CButton>
        </div>
        <div
          v-if="expandedSectionId === section.id"
          v-element-visibility="(isVisible: boolean) => onSectionVisibility?.(isVisible, section)"
          class="canvas-section__content rounded-2 pa-4"
          :style="{ ...getSectionThemeStyle(section) }"
        >
          <template v-for="c in sectionContentById[section.id]" :key="c.id">
            <div
              v-if="shouldRenderComponentWrapper(c, section.id)"
              v-element-visibility="(isVisible: boolean) => onSectionElementVisibility?.(isVisible, c)"
              class="component-wrapper relative"
            >
              <LinkableComponent :id="c.id" :used-in-section-id="section.id">
                <Component
                  :is="componentMap[c.type]"
                  v-bind="c.type === ComponentTypes.Grid ? c : omit(c, 'children')"
                  :used-in-section-id="section.id"
                />
              </LinkableComponent>
            </div>
          </template>
        </div>
      </SectionInfoProvider>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect, inject, onBeforeUnmount, type ComputedRef } from 'vue'
import { omit } from 'lodash-es'
import { vElementVisibility } from '@vueuse/components'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import Grid from '@canvas-builder/components/ui/Grid/Grid.vue'
import Carousel from '@canvas-builder/components/ui/Carousel/Carousel.vue'
import ContentGrid from '@canvas-builder/components/ui/ContentGrid/ContentGrid.vue'
import DataCharts from '@canvas-builder/components/ui/DataCharts/DataCharts.vue'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import Embeddable from '@canvas-builder/components/ui/Embeddable/Embeddable.vue'
import IFrame from '@canvas-builder/components/ui/IFrame/IFrame.vue'
import CollectionPlayer from '@canvas-builder/components/ui/CollectionPlayer/CollectionPlayer.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import Html from '@canvas-builder/components/ui/Html/Html.vue'
import Link from '@canvas-builder/components/ui/Link/Link.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import Timeline from '@canvas-builder/components/ui/Timeline/Timeline.vue'
import SectionInfoProvider from '@canvas-builder/components/container/SectionInfoProvider/SectionInfoProvider.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import useCollapsibles from '@canvas-builder/composables/useCollapsibles'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import { type CanvasBuilderProps, ComponentTypes, type ComponentNode } from '@canvas-builder/types/canvas'
import type { CanvasSection, CanvasRetrieve } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import SectionListExecution from '@canvas-builder/components/ui/SectionList/Execution/SectionListExecution.vue'
import type { AppFile } from '@lib/types/app'
import LinkableComponent from '@canvas-builder/components/container/LinkableComponent/LinkableComponent.vue'
import { DEFAULT_FONT } from '@canvas-builder/constants'
import useCanvasCompletionWizard from '@canvas-builder/composables/useCanvasCompletionWizard'

const componentMap = {
  Grid,
  Carousel,
  ContentGrid,
  DataCharts,
  DynamicDataTable,
  Embeddable,
  Text,
  Html,
  IFrame,
  Link,
  Multimedia,
  Timeline,
  CollectionPlayer,
} as Record<ComponentTypes, any>

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    sections?: { section: CanvasSection & { thumbnail_url?: string }; index: number }[]
    onSectionElementVisibility?: (isVisible: boolean, c: ComponentNode) => void
    onSectionVisibility?: (isVisible: boolean, section: CanvasSection) => void
    sectionListName?: string
  }>(),
  {
    sections: () => [],
  },
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const canvasSectionExecutionApp = inject<ComputedRef<AppFile | null>>(
  'canvasSectionExecutionApp',
  computed(() => null),
)
const { autofillSectionContentGridsWithMatchingProperties } = useRecommendations()
const expandedSectionId = ref<CanvasSection['id']>('')
const themeVars = useThemeVars()
const { activeCanvas, isEditMode, componentSelectedMode, listFiles, listRecommendedFiles, mode, saveCanvasContent } =
  useCanvas()
const { maintainSectionTheme } = useCanvasTheme()
const { shouldRenderComponentWrapper } = useCanvasVisibility()
const { listHeaderHtmlBySectionId } = useRecommendations()
const { clickedOnSection, resetClickedOnSection } = useCollapsibles()
const { isCompletionWizardEnabled } = useCanvasCompletionWizard()

const defaultSectionHeaderCollapsedStyle = computed(() => {
  return activeCanvas.value?.theme?.typography?.h_1
    ? {
        color: activeCanvas.value.theme.typography.h_1.color,
        fontFamily: activeCanvas.value.theme.typography.h_1.font_family,
      }
    : { fontFamily: activeCanvas.value?.theme?.font?.name || DEFAULT_FONT }
})

const defaultSectionHeaderExpandedStyle = computed(() => {
  const baseStyle = {
    fontSize: '20px',
  }

  if (maintainSectionTheme.value) {
    return baseStyle
  }

  return activeCanvas.value?.theme?.typography?.h_1
    ? {
        ...baseStyle,
        color: activeCanvas.value.theme.typography.h_1.color,
        fontFamily: activeCanvas.value.theme.typography.h_1.font_family,
      }
    : { ...baseStyle, fontFamily: activeCanvas.value?.theme?.font?.name || DEFAULT_FONT }
})

const sectionContentById = computed(() =>
  props.sections.reduce(
    (acc, sectionData) => {
      const { section } = sectionData
      acc[section.id] = section.content?.data ?? []
      return acc
    },
    {} as Record<CanvasSection['id'], ComponentNode[]>,
  ),
)
const collapseRef = ref<HTMLDivElement>()

function getSectionThemeStyle(section: CanvasRetrieve['sections'][number] & { theme?: CanvasRetrieve['theme'] }) {
  if (launchDarkly.value.enable_themes && (!maintainSectionTheme.value || section.theme?.inherit_theme_from_parent)) {
    return { backgroundColor: 'transparent' }
  }

  return { background: section?.theme?.canvas_bg_color ?? themeVars.value.base }
}

function maybeAutofillOnSectionOpen() {
  if (
    !launchDarkly.value.allow_content_grid_autofill ||
    activeCanvas.value?.is_template ||
    mode.value === 'sharing' ||
    !expandedSectionId.value ||
    isCompletionWizardEnabled.value
  ) {
    return
  }

  autofillSectionContentGridsWithMatchingProperties(expandedSectionId.value, {
    filesFetcher: listFiles.value as NonNullable<CanvasBuilderProps['listFiles']>,
    recommendedFilesFetcher: listRecommendedFiles.value as NonNullable<CanvasBuilderProps['listRecommendedFiles']>,
    instanceSettings: pitcherSettings.value,
  }).then(() => saveCanvasContent({ showToast: false }))
}

function onUpdateExpandedNames(names: CanvasSection['id'][]) {
  expandedSectionId.value = names?.length ? names[0] : ''

  maybeAutofillOnSectionOpen()

  if (collapseRef.value) {
    const activeCollapsible = collapseRef.value?.querySelector(`[data-section-id="${expandedSectionId.value}"]`)

    if (activeCollapsible) {
      setTimeout(() => {
        activeCollapsible.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' })
      }, 250)
    }
  }
}

function toggleSection(sectionId: CanvasSection['id']) {
  expandedSectionId.value = expandedSectionId.value === sectionId ? '' : sectionId
  onUpdateExpandedNames([expandedSectionId.value])
}

watchEffect(() => {
  if (clickedOnSection.value && clickedOnSection.value.sectionListId === props.id) {
    if (clickedOnSection.value.sectionId && expandedSectionId.value !== clickedOnSection.value.sectionId) {
      expandedSectionId.value = clickedOnSection.value.sectionId

      maybeAutofillOnSectionOpen()
    }

    resetClickedOnSection()
  }
})

onBeforeUnmount(() => {
  resetClickedOnSection()
})
</script>
